/**
 * Grips4Less custom build configurator (v2)
 * ------------------------------------------
 * - Renders the shaft's options as pill buttons (single select), replacing the
 *   theme variant picker.
 * - "Per-club" shafts (data.clubOption > 0): renders Club Numbers pills (multi
 *   select) from that option's values; each club resolves to its own variant.
 *   A value like "#9 iron / wedge" becomes #9 iron, PW, GW, SW, LW buttons that
 *   share that variant. Otherwise clubs come from the build-type checkboxes and
 *   all clubs use the one selected variant.
 * - Lists selected shafts with a total and mirrors that total in the main price.
 * - "Need help installing these shafts? (Add retrofit service)" toggle: closed =
 *   Add to cart adds just the shafts (plain lines); open = shows the retrofit panel
 *   and adds the whole build below.
 * - Quantity mode (data.qtyMode, .370 / wedge shafts with no per-club option): shafts only =
 *   the Quantity box next to Add to cart; with the retrofit add-on, the Iron # buttons in the
 *   clubhead step set the quantity (one shaft per club picked).
 * - Adds the build in ONE /cart/add.js call. Every line carries _build_id.
 *     children first: grip / grip tape / adapter / FLO / shipping protection / labor (qty = total clubs)
 *     then extra shaft lines (_build_role: shaft_club)
 *     then the lead shaft line last (_build_role: shaft, holds the build specs)
 *   Never touches cart attributes.
 */
(() => {
  if (customElements.get('build-configurator')) return;

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const NONE = '__none__';
  const WEDGE_CLUBS = ['PW', 'GW', 'SW', 'LW'];

  /* ---------- helpers ---------- */

  function formatMoney(cents, format) {
    const fmt = format || '${{amount}}';
    const value = Number(cents) || 0;
    const withDelims = (num, decimals, thousands, decimal) => {
      const fixed = (num / 100).toFixed(decimals);
      const [whole, frac] = fixed.split('.');
      const w = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? `${w}${decimal}${frac}` : w;
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return withDelims(value, 0, ',', '.');
        case 'amount_with_comma_separator': return withDelims(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelims(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return withDelims(value, 2, "'", '.');
        default: return withDelims(value, 2, ',', '.');
      }
    });
  }

  function makeBuildId() {
    let raw;
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      raw = window.crypto.randomUUID();
    } else if (window.crypto && window.crypto.getRandomValues) {
      raw = Array.from(window.crypto.getRandomValues(new Uint8Array(8)))
        .map((b) => b.toString(16).padStart(2, '0')).join('');
    } else {
      raw = `${Date.now().toString(16)}${Math.random().toString(16).slice(2)}`;
    }
    return raw.replace(/-/g, '').slice(0, 12).toUpperCase();
  }

  function option(value, label, disabled) {
    const o = document.createElement('option');
    o.value = value;
    o.textContent = label;
    if (disabled) o.disabled = true;
    return o;
  }

  function el(tag, cls, text) {
    const e = document.createElement(tag);
    if (cls) e.className = cls;
    if (text != null) e.textContent = text;
    return e;
  }

  /** Units that can still be bought for a variant, or Infinity when not limited. */
  function buyableQty(v) {
    if (!v) return 0;
    if (!v.available) return 0;
    if (v.tracked && v.policy === 'deny') return Math.max(0, Number(v.qty) || 0);
    return Infinity;
  }

  // A playing length in parentheses, e.g. "#4 iron (38”)", "Wedge / (35.5”)", "(37 in)".
  const LENGTH_IN_VALUE = /\(\s*(\d{2}(?:\.\d+)?)\s*(?:["”″]|in(?:ch(?:es)?)?\.?)?\s*\)/i;

  // A shaft weight at the end of a Flex value, e.g. "R300 (regular) / 127g", "S - 127 grams".
  const WEIGHT_IN_VALUE = /\s*[/–-]?\s*(\d{2,3}(?:\.\d+)?)\s*(?:g|grams?)\s*$/i;

  /** Club buttons for one Length/Club option value. */
  function clubButtonsFor(value) {
    // Value with a length in parentheses: the club name on the button, the length in small grey text
    // under it. "Wedge / (35.5”)" -> PW, GW, SW, LW, each with 35.5" (all share this variant).
    const lm = value.match(LENGTH_IN_VALUE);
    if (lm) {
      const len = `${lm[1]}"`;
      const name = value.replace(LENGTH_IN_VALUE, ' ').replace(/[\s/–-]+$/, '').replace(/\s+/g, ' ').trim();
      if (/^#?\s*(w|wedges?)$/i.test(name)) {
        return WEDGE_CLUBS.map((club) => ({ key: `${value}::${club}`, value, club, main: club, sub: len }));
      }
      if (/wedge/i.test(name) && name.includes('/')) { // "#9 iron / wedge (35.5”)"
        const base = name.split('/')[0].trim();
        return [base, ...WEDGE_CLUBS].map((club) => ({ key: `${value}::${club}`, value, club, main: club, sub: len }));
      }
      return [{ key: value, value, club: name || value, main: name || value, sub: len }];
    }
    // "#9 iron / wedge" -> #9 iron, PW, GW, SW, LW (all share this variant)
    if (/wedge/i.test(value) && value.includes('/')) {
      const base = value.split('/')[0].trim();
      return [base, ...WEDGE_CLUBS].map((club) => ({ key: `${value}::${club}`, value, club, main: club, sub: '' }));
    }
    const m = value.match(/^(.+?)\s+-\s+(.+)$/); // "#4 - 40\"" -> "#4" + "40\""
    const clubPart = (m ? m[1] : value).trim();
    const lengthPart = m ? m[2] : '';
    // "#W - 37\"" (or "W", "Wedge", "Wedges") -> PW, GW, SW, LW (all share this variant)
    if (/^#?\s*(w|wedges?)$/i.test(clubPart)) {
      return WEDGE_CLUBS.map((club) => ({ key: `${value}::${club}`, value, club, main: club, sub: lengthPart }));
    }
    return [{ key: value, value, club: clubPart, main: clubPart, sub: lengthPart }];
  }

  /*
   * Tip trim for the build team's "01_PREP TIP" instruction (data.tipTrim, custom-build-370 template).
   * Picked by the shaft's brand (product vendor, matched at the start of the name, so "KBS Tour" is KBS).
   * Any other brand: "See Manufacturer tipping instructions".
   */
  const TIP_TRIM = [
    {
      brands: /^(true temper|project x|kbs|aerotech)\b/i,
      lengths: { '2i': '0.5', '3i': '1.0', '4i': '1.5', '5i': '2.0', '6i': '2.5', '7i': '3.0', '8i': '3.5', '9i': '4.0', PW: '4.0', GW: '4.0', SW: '4.0', LW: '4.0' }
    },
    {
      brands: /^(fujikura|ust|mitsubishi)\b/i,
      lengths: { '2i': '0.0', '3i': '0.5', '4i': '1.0', '5i': '1.5', '6i': '2.0', '7i': '2.5', '8i': '3.0', '9i': '3.5', PW: '3.5', GW: '3.5', SW: '3.5', LW: '3.5' }
    }
  ];
  const TIP_TRIM_OTHER = 'See Manufacturer tipping instructions';

  /** "3i" / "#3 iron" / "3" -> { key: '3i', label: '3-Iron' }; "PW" -> { key: 'PW', label: 'PW' }. */
  function trimClub(club) {
    const c = String(club || '').trim();
    const m = c.match(/^#?\s*(\d{1,2})\s*(?:i|iron)?$/i);
    if (m) return { key: `${Number(m[1])}i`, label: `${Number(m[1])}-Iron` };
    return { key: c.toUpperCase(), label: c.toUpperCase() };
  }

  /** "3-Iron 1.0\" / 4-Iron 1.5\" / PW 4.0\"" for the clubs picked, or the manufacturer note. */
  function tipTrimText(vendor, clubs) {
    const table = TIP_TRIM.find((t) => t.brands.test(String(vendor || '').trim()));
    if (!table) return TIP_TRIM_OTHER;
    return clubs.map((club) => {
      const { key, label } = trimClub(club);
      return table.lengths[key] ? `${label} ${table.lengths[key]}"` : `${label} ${TIP_TRIM_OTHER}`;
    }).join(' / ');
  }

  /* ---------- clubhead lookup (assets/iron_heads.json) and tip sizes ---------- */

  const HEAD_RESULTS_MAX = 50; // rows drawn at once; typing more narrows the list
  const TEXT_US = { label: '408-475-7570', href: 'sms:+14084757570' };
  const jsonCache = new Map(); // url -> Promise<JSON | null>

  function loadJson(url) {
    if (!url) return Promise.resolve(null);
    if (!jsonCache.has(url)) {
      jsonCache.set(url, fetch(url)
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null));
    }
    return jsonCache.get(url);
  }

  /** '0.355" Tip' -> '0.355', '.370' -> '0.370', anything else -> ''. */
  function tipKey(t) {
    const m = String(t || '').match(/(?:^|[^\d])0?\.3(55|70)(?!\d)/);
    return m ? `0.3${m[1]}` : '';
  }

  /** Lowercase letters and digits only, so "JPX923" finds "JPX 923". */
  function squash(s) {
    return String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  /** "2013-2013" -> "2013", "2019-2021" stays as is. */
  function headYears(y) {
    const m = String(y || '').match(/^(\d{4})\s*-\s*(\d{4})$/);
    if (m) return m[1] === m[2] ? m[1] : `${m[1]}-${m[2]}`;
    return String(y || '').trim();
  }

  /**
   * A shaft title without its tip size, so the .355 and .370 versions of the same
   * shaft match: "KBS Tour V-X Lite Iron Shaft Taper Tip (.355)" and
   * "KBS Tour V-X Lite Iron Shaft Parallel Tip (.370)" -> "kbs tour v x lite".
   * Taper means a 0.355" tip and Parallel a 0.370" tip, so those words are dropped too
   * ("Mitsubishi MMT Taper" and "Mitsubishi MMT Parallel" -> "mitsubishi mmt").
   */
  function shaftFamily(title) {
    return String(title || '').toLowerCase()
      .replace(/0?\.3(55|70)\s*("|in(ch)?\b)?/g, ' ')
      .replace(/\b(tapered|taper|parallel|tip)\b/g, ' ')
      .replace(/\b(iron|steel|shaft|graphite)s?\b/g, ' ')
      .replace(/[()\-–,"]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /** Flattens iron_heads.json into one searchable list (irons and wedges). */
  function headEntries(db) {
    const out = [];
    if (!db || typeof db !== 'object') return out;
    [['models', false], ['wedges', true]].forEach(([key, wedge]) => {
      const byBrand = db[key];
      if (!byBrand || typeof byBrand !== 'object') return;
      Object.keys(byBrand).forEach((brand) => {
        if (!Array.isArray(byBrand[brand])) return;
        byBrand[brand].forEach((m) => {
          if (!m || !m.model) return;
          const years = headYears(m.years);
          const hay = `${brand} ${m.model} ${years} ${wedge ? 'wedge' : 'iron'}`.toLowerCase();
          out.push({
            id: out.length,
            brand,
            model: String(m.model),
            years,
            end: Number((years.match(/(\d{4})$/) || [])[1]) || 0,
            wedge,
            tip: m.answer === true ? tipKey(m.tip) : '', // only trust a confirmed answer
            hay,
            sq: squash(hay)
          });
        });
      });
    });
    return out;
  }

  /** "Mizuno JPX 923 Tour (2022)", wedges: "Cleveland RTX 6 ZipCore wedge (2021)". */
  function headLabel(h) {
    const wedge = h.wedge && !/wedge/i.test(h.model) ? ' wedge' : '';
    return `${h.brand} ${h.model}${wedge}${h.years ? ` (${h.years})` : ''}`;
  }

  /**
   * Club buttons -> short iron #s, e.g. #4 iron ... #9 iron, PW -> "4-PW";
   * #5, #7, PW, GW -> "5, 7, PW, GW".
   */
  function clubsSummary(clubs) {
    const names = clubs.map((c) => {
      const m = String(c).match(/^#?\s*(\d{1,2})(?:i\b|\b)/i); // "#4 iron", "#4", "4i"
      return m ? Number(m[1]) : String(c).trim();
    });
    const nums = names.filter((n) => typeof n === 'number');
    const rest = names.filter((n) => typeof n !== 'number');
    const parts = [];
    let i = 0;
    while (i < nums.length) {
      let j = i;
      while (j + 1 < nums.length && nums[j + 1] === nums[j] + 1) j++;
      if (j - i >= 2) parts.push(`${nums[i]}-${nums[j]}`);
      else for (let k = i; k <= j; k++) parts.push(String(nums[k]));
      i = j + 1;
    }
    // A run that ends at 9 and goes straight into PW reads as "4-PW".
    const lastNum = nums[nums.length - 1];
    if (lastNum === 9 && rest[0] === 'PW' && parts.length && parts[parts.length - 1].includes('-')) {
      parts[parts.length - 1] = `${parts[parts.length - 1].split('-')[0]}-PW`;
      rest.shift();
    }
    return [...parts, ...rest].join(', ');
  }

  /* ---------- grip photo: find the grip in a product photo and lay it flat ---------- */

  const PHOTO_SIZE = 640; // px the photo is loaded and measured at
  const productJsonCache = new Map(); // handle -> Promise<product JSON | null>
  const flatPhotoCache = new Map(); // image URL -> Promise<canvas | null>

  function productJson(handle) {
    if (!productJsonCache.has(handle)) {
      productJsonCache.set(handle, fetch(`${ROOT}products/${encodeURIComponent(handle)}.js`)
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null));
    }
    return productJsonCache.get(handle);
  }

  function sizedImageUrl(src, width) {
    const u = new URL(src, window.location.href);
    u.searchParams.set('width', String(width));
    return u.toString();
  }

  function loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous'; // needed to read the pixels (Shopify's CDN allows it)
      img.decoding = 'async';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  }

  /**
   * Finds the single grip in a product photo, turns it horizontal with the butt
   * end on the left and crops it tight. Returns a canvas, or null when the photo
   * is not one whole grip (several grips side by side, a close-up crop, etc.).
   */
  function flattenGrip(img) {
    const S = PHOTO_SIZE;
    const src = document.createElement('canvas');
    src.width = S;
    src.height = S;
    const ctx = src.getContext('2d', { willReadFrequently: true });
    const k = Math.min(S / img.naturalWidth, S / img.naturalHeight);
    const w = img.naturalWidth * k;
    const h = img.naturalHeight * k;
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, S, S);
    ctx.drawImage(img, (S - w) / 2, (S - h) / 2, w, h);
    const px = ctx.getImageData(0, 0, S, S).data;
    const at = (x, y) => (y * S + x) * 4;

    // Background: the middle value of the four corners (most photos are white, a few light grey).
    const corners = [[2, 2], [S - 3, 2], [2, S - 3], [S - 3, S - 3]].map(([x, y]) => at(x, y));
    const bg = [0, 1, 2].map((c) => corners.map((o) => px[o + c]).sort((a, b) => a - b)[1]);

    // Grip pixels = anything clearly different from the background. Collect position moments.
    const mask = new Uint8Array(S * S);
    let n = 0; let sx = 0; let sy = 0; let sxx = 0; let syy = 0; let sxy = 0;
    for (let y = 0; y < S; y++) {
      for (let x = 0; x < S; x++) {
        const o = at(x, y);
        if (Math.abs(px[o] - bg[0]) + Math.abs(px[o + 1] - bg[1]) + Math.abs(px[o + 2] - bg[2]) > 60) {
          mask[y * S + x] = 1;
          n++; sx += x; sy += y; sxx += x * x; syy += y * y; sxy += x * y;
        }
      }
    }
    if (n < S) return null;

    // Main axis of the shape (the grip's length direction) and how elongated it is.
    const mx = sx / n; const my = sy / n;
    const vxx = sxx / n - mx * mx; const vyy = syy / n - my * my; const vxy = sxy / n - mx * my;
    const angle = 0.5 * Math.atan2(2 * vxy, vxx - vyy);
    const spread = Math.sqrt(((vxx - vyy) / 2) ** 2 + vxy * vxy);
    const elongation = Math.sqrt(((vxx + vyy) / 2 + spread) / Math.max((vxx + vyy) / 2 - spread, 1e-6));
    const ux = Math.cos(angle); const uy = Math.sin(angle);

    // Length and thickness along that axis, how much sits at each end, and edge contact.
    let tMin = Infinity; let tMax = -Infinity; let vMin = Infinity; let vMax = -Infinity; let edge = 0;
    for (let y = 0; y < S; y++) {
      for (let x = 0; x < S; x++) {
        if (mask[y * S + x]) {
          const t = (x - mx) * ux + (y - my) * uy;
          const v = (y - my) * ux - (x - mx) * uy;
          if (t < tMin) tMin = t;
          if (t > tMax) tMax = t;
          if (v < vMin) vMin = v;
          if (v > vMax) vMax = v;
          if (x === 0 || y === 0 || x === S - 1 || y === S - 1) edge++;
        }
      }
    }
    const len = tMax - tMin;
    const thick = vMax - vMin;
    // One whole grip: long and thin, and not cut off by the photo's edges.
    if (elongation < 5 || len / Math.max(thick, 1) < 5 || edge > S * 0.1) return null;

    let endA = 0; let endB = 0;
    for (let y = 0; y < S; y++) {
      for (let x = 0; x < S; x++) {
        if (mask[y * S + x]) {
          const t = (x - mx) * ux + (y - my) * uy;
          if (t < tMin + len * 0.2) endA++;
          else if (t > tMax - len * 0.2) endB++;
        }
      }
    }
    const flip = endB > endA; // the butt end is the thicker one; put it on the left

    const out = document.createElement('canvas');
    out.width = Math.ceil(len) + 8;
    out.height = Math.ceil(thick) + 8;
    const o = out.getContext('2d');
    o.fillStyle = `rgb(${bg[0]}, ${bg[1]}, ${bg[2]})`;
    o.fillRect(0, 0, out.width, out.height);
    const tMid = (tMin + tMax) / 2; const vMid = (vMin + vMax) / 2;
    o.translate(out.width / 2, out.height / 2);
    o.rotate(-angle + (flip ? Math.PI : 0));
    o.translate(-(mx + tMid * ux - vMid * uy), -(my + tMid * uy + vMid * ux));
    o.drawImage(src, 0, 0);
    return out;
  }

  function flatPhoto(src) {
    const url = sizedImageUrl(src, PHOTO_SIZE);
    if (!flatPhotoCache.has(url)) {
      flatPhotoCache.set(url, loadImage(url).then(flattenGrip).catch(() => null));
    }
    return flatPhotoCache.get(url);
  }

  /**
   * Horizontal photo of a grip. A chosen size / color with its own photo uses that
   * photo only (so the color matches); otherwise the product's photos are tried in order.
   */
  async function gripPhoto(grip, variantId) {
    const p = await productJson(grip.handle);
    if (!p) return null;
    const variants = p.variants || [];
    const v = variants.find((x) => x.id === variantId) || (variants.length === 1 ? variants[0] : null);
    const srcs = v && v.featured_image ? [v.featured_image.src] : (p.images || []).slice(0, 4);
    for (const src of srcs) {
      const canvas = await flatPhoto(src); // eslint-disable-line no-await-in-loop
      if (canvas) return canvas;
    }
    return null;
  }

  /* ---------- element ---------- */

  class BuildConfigurator extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      try {
        this.data = JSON.parse(this.querySelector('[data-build-data]').textContent);
      } catch (e) {
        console.error('[build] Could not read builder data', e); // eslint-disable-line
        return;
      }

      const d = this.data;
      this.section = this.closest('.js-product') || document;
      this.clubPos = Number(d.clubOption) || 0; // 1-based option position, 0 = not per-club
      this.qtyMode = d.qtyMode === true && !this.clubPos; // sold by quantity; Iron #s picked in the retrofit panel
      // Clubhead step wording: wedge builds talk about wedges (matches the snippet's head_* wording).
      const wedgeBuild = d.buildType === 'Wedge';
      this.words = {
        clubs: wedgeBuild ? 'wedges' : 'irons',
        nums: wedgeBuild ? 'wedges' : 'iron #s'
      };
      this.busy = false;

      // Selected value per option position (1-based), seeded from the current variant.
      const start = d.shaft.variants.find((v) => v.id === d.shaft.variantId) || d.shaft.variants[0];
      this.selected = {};
      d.shaft.options.forEach((o) => {
        if (o.position !== this.clubPos) this.selected[o.position] = start ? start.options[o.position - 1] : o.values[0];
      });

      this.clubButtons = this.clubPos
        ? d.shaft.options.find((o) => o.position === this.clubPos).values.flatMap(clubButtonsFor)
        : [];
      this.selectedClubs = new Set(); // keys of clubButtons

      this.el = {
        optionRows: this.querySelector('.js-build-option-rows'),
        clubGrid: this.querySelector('.js-build-club-grid'),
        shafts: this.querySelector('.js-build-shafts'),
        shaftsList: this.querySelector('.js-build-shafts-list'),
        shaftsCount: this.querySelector('.js-build-shafts-count'),
        shaftsPrice: this.querySelector('.js-build-shafts-price'),
        clubs: Array.from(this.querySelectorAll('.js-build-club')),
        quick: Array.from(this.querySelectorAll('.js-build-quickset')),
        length: this.querySelector('.js-build-length'),
        brand: this.querySelector('.js-build-grip-brand'),
        model: this.querySelector('.js-build-grip-model'),
        gripSearch: this.querySelector('.js-build-grip-search'),
        gripVariant: this.querySelector('.js-build-grip-variant'),
        gripNote: this.querySelector('.js-build-grip-note'),
        gripExtras: this.querySelector('.js-build-grip-extras'),
        logo: this.querySelector('.js-build-grip-logo'),
        tape: this.querySelector('.js-build-tape'),
        removed: this.querySelector('.js-build-removed'),
        adapter: this.querySelector('.js-build-adapter'),
        notes: this.querySelector('.js-build-notes'),
        notesCount: this.querySelector('.js-build-notes-count'),
        flo: this.querySelector('.js-build-flo'),
        clubhead: this.querySelector('.js-build-clubhead'),
        headSearch: this.querySelector('.js-build-head-search'),
        headSwitch: this.querySelector('.js-build-head-switch'),
        headTip: this.querySelector('.js-build-head-tip'),
        headClubs: this.querySelector('.js-build-head-clubs'),
        protection: Array.from(this.querySelectorAll('.js-build-protection')),
        ackCancel: this.querySelector('.js-build-ack-cancel'),
        ackTurnaround: this.querySelector('.js-build-ack-turnaround'),
        lines: this.querySelector('.js-build-lines'),
        total: this.querySelector('.js-build-total'),
        error: this.querySelector('.js-build-error'),
        add: this.querySelector('.js-build-add'),
        hint: this.querySelector('.js-build-hint'),
        retrofitToggle: this.querySelector('.js-build-retrofit-toggle'),
        panel: this.querySelector('.js-build-panel'),
        mainPrice: this.section.querySelector('.product-info__price .price__current'),
        qty: this.querySelector('.js-build-qty'),
        qtyMinus: this.querySelector('.js-build-qty-minus'),
        qtyPlus: this.querySelector('.js-build-qty-plus'),
        ironsRequired: this.querySelector('.js-build-irons-required'),
        specs: this.querySelector('.js-build-specs')
      };

      // Longest build note we keep; the textarea's maxlength stops typing at the same point.
      this.notesMax = Number(this.el.notes.getAttribute('maxlength')) || 250;

      // Retrofit (grip + assembly) is opt-in when there is a toggle; otherwise the panel is always on.
      this.retrofit = !this.el.retrofitToggle;

      // Shaft stock problems (e.g. too few of a shared wedge shaft) show right under the club buttons.
      if (this.el.clubGrid) {
        this.el.shaftNote = el('p', 'build-config__note build-shafts__note');
        this.el.shaftNote.setAttribute('role', 'alert');
        this.el.shaftNote.hidden = true;
        this.el.clubGrid.closest('.build-field').insertAdjacentElement('afterend', this.el.shaftNote);
      }
      // Quantity mode: the same note, in red, right under the Iron # / wedge buttons in the retrofit panel.
      const headClubs = this.qtyMode ? this.querySelector('.build-head .build-config__clubs') : null;
      if (headClubs) {
        this.el.shaftNote = el('p', 'build-config__note build-shafts__note build-head__stock');
        this.el.shaftNote.setAttribute('role', 'alert');
        this.el.shaftNote.hidden = true;
        headClubs.insertAdjacentElement('afterend', this.el.shaftNote);
      }

      this.renderOptionRows();
      this.renderSpecs();
      this.renderClubGrid();
      this.populateBrands();
      this.populateAdapters();
      this.enhanceChoices();
      this.setupGripPicker();
      this.setupHeadPicker();
      this.bind();
      this.update();
    }

    /* ----- shaft options + club pills ----- */

    /** Variant for the selected non-club options plus (optionally) one club value. */
    variantFor(clubValue) {
      return this.data.shaft.variants.find((v) => v.options.every((val, i) => {
        const pos = i + 1;
        if (pos === this.clubPos) return val === clubValue;
        return val === this.selected[pos];
      })) || null;
    }

    /** True when some available variant has this value and the other non-club selections. */
    optionValueAvailable(pos, value) {
      return this.data.shaft.variants.some((v) => v.available && v.options.every((val, i) => {
        const p = i + 1;
        if (p === pos) return val === value;
        if (p === this.clubPos) return true;
        return val === this.selected[p];
      }));
    }

    renderOptionRows() {
      const wrap = this.el.optionRows;
      wrap.innerHTML = '';
      this.data.shaft.options.forEach((o) => {
        if (o.position === this.clubPos) return;
        const field = el('div', 'build-field');
        if (o.values.length === 1) field.hidden = true; // e.g. a single Tip Size
        field.appendChild(el('div', 'build-field__label', o.name));
        const grid = el('div', 'build-btn-grid');
        grid.setAttribute('role', 'group');
        grid.setAttribute('aria-label', o.name);
        o.values.forEach((val) => {
          // spec_line pages: "R300 (regular) / 127g" -> R300 (REGULAR) with 127g in small grey text under it.
          // A value that is only a weight ("85g") stays as it is, in the button's normal font.
          const wm = this.data.specLine ? val.match(WEIGHT_IN_VALUE) : null;
          const flexName = wm ? val.replace(WEIGHT_IN_VALUE, '').trim() : '';
          const b = el('button', 'build-btn', flexName ? null : val);
          if (flexName) {
            b.appendChild(el('span', 'build-btn__club', flexName));
            b.appendChild(el('span', 'build-btn__sub', `${wm[1]}g`));
          }
          b.type = 'button';
          b.dataset.optionPos = o.position;
          b.dataset.value = val;
          b.setAttribute('aria-pressed', String(this.selected[o.position] === val));
          if (this.selected[o.position] === val) b.classList.add('is-selected');
          if (!this.optionValueAvailable(o.position, val)) {
            b.classList.add('is-unavailable');
            b.title = 'Sold out';
          }
          grid.appendChild(b);
        });
        field.appendChild(grid);
        wrap.appendChild(field);
      });
    }

    /**
     * Spec block under the shaft options (spec_line, quantity mode), for the selected flex, e.g.
     *   0.370" parallel tip · 41" raw length
     *   Tip trim and cut to fit any club 3-iron through wedge.
     * Raw length comes from the Length option ("41\""). The weight is shown on the Flex buttons instead.
     * Anything not there is left out.
     */
    renderSpecs() {
      const box = this.el.specs;
      if (!box) return;
      const v = this.variantFor(null);
      const opts = this.data.shaft.options;
      const valueOf = (test) => {
        const o = v && opts.find((x) => test.test(x.name));
        return o ? String(v.options[o.position - 1] || '') : '';
      };
      const lenMatch = valueOf(/length/i).match(/(\d{2}(?:\.\d+)?)/);
      const len = lenMatch ? String(Math.round(parseFloat(lenMatch[1]) * 100) / 100) : '';
      const tip = v ? tipKey(v.tip) || tipKey(valueOf(/tip/i)) : '';

      const facts = [
        tip && `${tip}" ${tip === '0.370' ? 'parallel' : 'taper'} tip`,
        len && `${len}" raw length`
      ].filter(Boolean);

      // Club range from the Iron # buttons, e.g. "3-iron through wedge".
      const clubs = this.el.clubs.map((c) => c.value);
      const name = (c) => {
        const m = String(c || '').match(/^(\d{1,2})i$/i);
        if (m) return `${m[1]}-iron`;
        return WEDGE_CLUBS.includes(String(c).toUpperCase()) ? 'wedge' : String(c || '');
      };
      const range = clubs.length > 1 ? ` ${name(clubs[0])} through ${name(clubs[clubs.length - 1])}` : '';

      box.textContent = '';
      if (facts.length) box.appendChild(el('p', 'build-specs__facts', facts.join(' · ')));
      // spec_note (template setting) replaces the default note, e.g. the wedge template's "Already pre-tipped...".
      box.appendChild(el('p', 'build-specs__note', this.data.specNote || `Tip trim and cut to fit any club${range}.`));
      box.hidden = false;
    }

    renderClubGrid() {
      if (!this.el.clubGrid) return;
      const grid = this.el.clubGrid;
      grid.innerHTML = '';
      this.clubButtons.forEach((c) => {
        const v = this.variantFor(c.value);
        const ok = buyableQty(v) > 0;
        if (!ok) this.selectedClubs.delete(c.key);
        const b = el('button', 'build-btn build-btn--club');
        b.type = 'button';
        b.dataset.clubKey = c.key;
        b.appendChild(el('span', 'build-btn__club', c.main));
        if (c.sub) b.appendChild(el('span', 'build-btn__sub', c.sub));
        b.disabled = !ok;
        if (!ok) b.title = 'Sold out in this flex';
        const on = this.selectedClubs.has(c.key);
        b.classList.toggle('is-selected', on);
        b.setAttribute('aria-pressed', String(on));
        grid.appendChild(b);
      });
    }

    /* ----- grips / adapters ----- */

    /** True when at least one variant of this grip product can be bought. */
    static gripInStock(g) {
      return g.variants.some((v) => buyableQty(v) > 0);
    }

    // Out-of-stock grips stay in the lists, greyed out and not selectable.
    populateBrands() {
      const brands = Array.from(new Set(this.data.grips.map((g) => g.vendor)))
        .sort((a, b) => a.localeCompare(b));

      const noneOpt = this.el.brand.querySelector(`option[value="${NONE}"]`);
      brands.forEach((b) => {
        const soldOut = !this.data.grips.some((g) => g.vendor === b && BuildConfigurator.gripInStock(g));
        this.el.brand.insertBefore(option(b, soldOut ? `${b} - sold out` : b, soldOut), noneOpt);
      });
    }

    populateAdapters() {
      if (!this.el.adapter) return;
      this.data.adapters.forEach((a) => {
        const soldOut = buyableQty(a) === 0;
        const label = `${a.title} (+${formatMoney(a.price, this.data.moneyFormat)})${soldOut ? ' - sold out' : ''}`;
        this.el.adapter.appendChild(option(String(a.id), label, soldOut));
      });
    }

    /** Brand changed: reload the brand's models and reopen the search list. */
    fillModels() {
      const brand = this.el.brand.value;
      const sel = this.el.model;
      const hasBrand = Boolean(brand) && brand !== NONE;
      sel.innerHTML = '';
      this.el.gripVariant.innerHTML = '';
      this.el.gripVariant.hidden = true;
      if (this.el.gripSearch) this.el.gripSearch.value = '';
      this.gripPickerOpen = true;

      if (hasBrand) {
        sel.appendChild(option('', 'Choose grip model'));
        this.brandModels().forEach((g) => {
          const soldOut = !BuildConfigurator.gripInStock(g);
          sel.appendChild(option(String(g.productId), soldOut ? `${g.title} - sold out` : g.title, soldOut));
        });
      }
      sel.hidden = !hasBrand;
      this.renderGripResults();
    }

    /** The chosen brand's grip models, A to Z. */
    brandModels() {
      const brand = this.el.brand.value;
      return this.data.grips
        .filter((g) => g.vendor === brand)
        .sort((a, b) => a.title.localeCompare(b.title));
    }

    /**
     * Grip model picker: a search box with a results list under it that narrows as
     * the customer types, plus a "selected grip" row once one is picked. The model
     * <select> stays in the page (not displayed) and keeps holding the value.
     */
    setupGripPicker() {
      const sel = this.el.model;
      if (!this.el.gripSearch) return;

      const status = el('p', 'build-results__status');
      status.setAttribute('aria-live', 'polite');
      const list = el('div', 'build-results');
      list.setAttribute('role', 'group');
      list.setAttribute('aria-label', 'Grip models');
      const picked = el('div', 'build-picked');
      const pickedName = el('span', 'build-picked__name');
      const change = el('button', 'build-picked__change', 'Change');
      change.type = 'button';
      picked.append(pickedName, change);

      // "More below" arrow over the bottom of the list while there is more to scroll to.
      const wrap = el('div', 'build-results-wrap');
      const more = el('button', 'build-results__more');
      more.type = 'button';
      more.tabIndex = -1; // keyboard users move through the list with the arrow keys
      more.setAttribute('aria-hidden', 'true');
      more.appendChild(el('span', 'build-results__more-pill', 'More below'));
      more.hidden = true;
      wrap.append(list, more);
      [status, wrap, picked].forEach((node) => { node.hidden = true; });

      // The chosen grip's photo, laid flat, under the selected-grip row (see showGripPhoto).
      const photo = el('div', 'build-grip-photo');
      photo.hidden = true;

      // After the search box: status line, results, the selected-grip row, then its photo.
      sel.insertAdjacentElement('afterend', photo);
      sel.insertAdjacentElement('afterend', picked);
      sel.insertAdjacentElement('afterend', wrap);
      sel.insertAdjacentElement('afterend', status);
      sel.classList.add('build-is-replaced');
      Object.assign(this.el, {
        gripStatus: status, gripResults: list, gripResultsWrap: wrap, gripMore: more, gripPicked: picked, gripPickedName: pickedName, gripPhoto: photo
      });
      this.gripPickerOpen = true;

      list.addEventListener('scroll', () => this.updateGripMore(), { passive: true });
      window.addEventListener('resize', () => this.updateGripMore());
      more.addEventListener('click', () => {
        list.scrollBy({ top: Math.round(list.clientHeight * 0.8), behavior: 'smooth' });
      });

      const rows = () => Array.from(list.querySelectorAll('.build-result:not(:disabled)'));

      list.addEventListener('click', (evt) => {
        const row = evt.target.closest('.build-result');
        if (!row || row.disabled) return;
        if (sel.value === row.dataset.id) { // same grip again: keep its size / color, just fold the list
          this.gripPickerOpen = false;
          this.renderGripResults();
          return;
        }
        sel.value = row.dataset.id;
        sel.dispatchEvent(new Event('change', { bubbles: true }));
      });

      // Arrow keys move through the results; Enter in the search box picks the first one.
      list.addEventListener('keydown', (evt) => {
        if (evt.key !== 'ArrowDown' && evt.key !== 'ArrowUp') return;
        const all = rows();
        const i = all.indexOf(document.activeElement);
        evt.preventDefault();
        if (evt.key === 'ArrowUp' && i <= 0) this.el.gripSearch.focus();
        else (all[i + (evt.key === 'ArrowDown' ? 1 : -1)] || all[i]).focus();
      });
      this.el.gripSearch.addEventListener('keydown', (evt) => {
        const all = rows();
        if (!all.length) return;
        if (evt.key === 'ArrowDown') { evt.preventDefault(); all[0].focus(); }
        if (evt.key === 'Enter') { evt.preventDefault(); all[0].click(); }
      });

      // Clicking anywhere on the selected-grip row reopens the list, not only its "Change" link.
      picked.addEventListener('click', () => {
        this.gripPickerOpen = true;
        this.el.gripSearch.value = ''; // show the whole list again
        this.renderGripResults();
        this.el.gripSearch.focus();
      });
    }

    /** Draws the results for what is typed in the search box, or the selected-grip row. */
    renderGripResults() {
      const list = this.el.gripResults;
      if (!list) return;
      const brand = this.el.brand.value;
      const hasBrand = Boolean(brand) && brand !== NONE;
      const picked = this.selectedGripProduct();
      const open = hasBrand && (this.gripPickerOpen || !picked);

      this.el.gripSearch.hidden = !open;
      this.el.gripStatus.hidden = !open;
      list.hidden = !open;
      this.el.gripResultsWrap.hidden = !open;
      this.el.gripPicked.hidden = !(hasBrand && picked && !open);
      this.showGripPhoto();
      if (picked) {
        // A grip with a single size / color shows it here, since there is no dropdown for it.
        const only = picked.variants.length === 1 ? picked.variants[0].title : '';
        this.el.gripPickedName.textContent = only && !/default title/i.test(only)
          ? `${picked.title} (${only})`
          : picked.title;
      }
      if (!open) {
        this.updateGripMore();
        return;
      }

      const fmt = (c) => formatMoney(c, this.data.moneyFormat);
      const terms = this.el.gripSearch.value.toLowerCase().split(/\s+/).filter(Boolean);
      const models = this.brandModels();
      const found = models.filter((g) => {
        const title = g.title.toLowerCase();
        return terms.every((t) => title.includes(t));
      });

      list.innerHTML = '';
      found.forEach((g) => {
        const soldOut = !BuildConfigurator.gripInStock(g);
        const isPicked = Boolean(picked) && picked.productId === g.productId;
        const prices = g.variants.map((v) => v.price);
        const low = Math.min(...prices);
        const row = el('button', `build-result${isPicked ? ' is-selected' : ''}`);
        row.type = 'button';
        row.dataset.id = String(g.productId);
        row.disabled = soldOut;
        row.setAttribute('aria-pressed', String(isPicked));
        row.appendChild(el('span', 'build-result__name', g.title));
        row.appendChild(el('span', 'build-result__meta',
          soldOut ? 'Sold out' : `${low !== Math.max(...prices) ? 'from ' : ''}${fmt(low)}`));
        list.appendChild(row);
      });

      if (!terms.length) {
        this.el.gripStatus.textContent = `${models.length} grip model${models.length === 1 ? '' : 's'}. Type above to narrow the list.`;
      } else if (found.length) {
        this.el.gripStatus.textContent = `${found.length} match${found.length === 1 ? '' : 'es'}`;
      } else {
        this.el.gripStatus.textContent = 'No grip models match your search.';
      }
      list.scrollTop = 0;
      this.updateGripMore();
    }

    /** Shows the "More below" arrow only while the list can still be scrolled down. */
    updateGripMore() {
      const list = this.el.gripResults;
      if (!list || !this.el.gripMore) return;
      const below = list.scrollHeight - list.clientHeight - list.scrollTop;
      this.el.gripMore.hidden = list.hidden || below < 8;
    }

    /* ----- clubhead picker (retrofit only) ----- */

    /**
     * Clubhead brand / model: a search box over iron_heads.json with a results list
     * that narrows as the customer types (like the grip picker), a "selected" row once
     * one is picked, and "My irons aren't listed" to type it in instead.
     * Iron #s are their own field, pre-filled from the club buttons until edited.
     */
    setupHeadPicker() {
      const search = this.el.headSearch;
      if (!search) return;
      this.head = null; // picked iron_heads.json entry
      this.headMode = 'list'; // 'list' = search the list, 'manual' = typed in
      this.headOpen = true;
      this.headClubsEdited = false;
      this.headClubsAuto = '';

      const status = el('p', 'build-results__status');
      status.setAttribute('aria-live', 'polite');
      const list = el('div', 'build-results');
      list.setAttribute('role', 'group');
      list.setAttribute('aria-label', 'Clubhead models');
      const wrap = el('div', 'build-results-wrap');
      const more = el('button', 'build-results__more');
      more.type = 'button';
      more.tabIndex = -1;
      more.setAttribute('aria-hidden', 'true');
      more.appendChild(el('span', 'build-results__more-pill', 'More below'));
      more.hidden = true;
      wrap.append(list, more);
      const picked = el('div', 'build-picked');
      const pickedName = el('span', 'build-picked__name');
      const change = el('button', 'build-picked__change', 'Change');
      change.type = 'button';
      picked.append(pickedName, change);
      [status, wrap, picked].forEach((node) => { node.hidden = true; });

      search.insertAdjacentElement('afterend', picked);
      search.insertAdjacentElement('afterend', wrap);
      search.insertAdjacentElement('afterend', status);
      Object.assign(this.el, {
        headStatus: status, headResults: list, headResultsWrap: wrap, headMore: more, headPicked: picked, headPickedName: pickedName
      });

      const updateMore = () => {
        const below = list.scrollHeight - list.clientHeight - list.scrollTop;
        more.hidden = wrap.hidden || below < 8;
      };
      this.updateHeadMore = updateMore;
      list.addEventListener('scroll', updateMore, { passive: true });
      window.addEventListener('resize', updateMore);
      more.addEventListener('click', () => {
        list.scrollBy({ top: Math.round(list.clientHeight * 0.8), behavior: 'smooth' });
      });

      const rows = () => Array.from(list.querySelectorAll('.build-result'));
      list.addEventListener('click', (evt) => {
        const row = evt.target.closest('.build-result');
        if (!row) return;
        this.head = this.headList[Number(row.dataset.i)] || null;
        this.headOpen = !this.head;
        this.renderHead();
        this.update();
        if (this.head && this.el.headClubs && !this.el.headClubs.value) this.el.headClubs.focus();
      });
      list.addEventListener('keydown', (evt) => {
        if (evt.key !== 'ArrowDown' && evt.key !== 'ArrowUp') return;
        const all = rows();
        const i = all.indexOf(document.activeElement);
        evt.preventDefault();
        if (evt.key === 'ArrowUp' && i <= 0) search.focus();
        else (all[i + (evt.key === 'ArrowDown' ? 1 : -1)] || all[i]).focus();
      });
      search.addEventListener('keydown', (evt) => {
        const all = rows();
        if (!all.length || list.hidden) return;
        if (evt.key === 'ArrowDown') { evt.preventDefault(); all[0].focus(); }
        if (evt.key === 'Enter') { evt.preventDefault(); all[0].click(); }
      });
      search.addEventListener('focus', () => this.loadHeads());
      search.addEventListener('input', () => {
        this.headOpen = true;
        this.renderHead();
      });

      // Clicking anywhere on the selected row reopens the search.
      picked.addEventListener('click', () => {
        this.headOpen = true;
        search.value = '';
        this.renderHead();
        search.focus();
      });

      // "My irons aren't listed" <-> "Search the list instead"
      this.el.headSwitch.addEventListener('click', () => {
        this.headMode = this.headMode === 'list' ? 'manual' : 'list';
        if (this.headMode === 'list') this.headOpen = !this.head; // show the one they picked before, if any
        if (this.headMode === 'manual' && !this.el.clubhead.value && search.value) {
          this.el.clubhead.value = search.value.trim().slice(0, Number(this.el.clubhead.maxLength) || 70);
        }
        this.renderHead();
        this.update();
        (this.headMode === 'manual' ? this.el.clubhead : search).focus();
      });

      // Iron #s follow the club buttons until the customer types their own.
      // (Quantity mode has Iron # buttons instead of this box.)
      if (this.el.headClubs) {
        this.el.headClubs.addEventListener('input', () => {
          const v = this.el.headClubs.value.trim();
          this.headClubsEdited = v !== '' && v !== this.headClubsAuto;
          this.update();
        });
      }

      this.renderHead();
    }

    /** Loads iron_heads.json once. If it can't be loaded, the customer types the clubhead in. */
    loadHeads() {
      if (!this.el.headSearch || this.headsPromise) return this.headsPromise;
      this.headsState = 'loading';
      this.headsPromise = loadJson(this.data.headsUrl).then((db) => {
        this.headList = headEntries(db);
        this.headsState = this.headList.length ? 'ready' : 'failed';
      }).catch(() => {
        this.headList = [];
        this.headsState = 'failed';
      }).then(() => {
        if (this.headsState === 'failed') this.headMode = 'manual';
        this.renderHead();
        this.update();
      });
      this.renderHead();
      return this.headsPromise;
    }

    /** Clubhead list matches for the search box, best first. */
    findHeads(query) {
      const terms = String(query || '').toLowerCase().split(/\s+/).filter(Boolean);
      if (!terms.length) return [];
      const sqTerms = terms.map(squash);
      const sqAll = squash(query);
      const found = [];
      (this.headList || []).forEach((h) => {
        const hit = terms.every((t, i) => h.hay.includes(t) || (sqTerms[i] && h.sq.includes(sqTerms[i])))
          || (sqAll.length > 2 && h.sq.includes(sqAll));
        if (!hit) return;
        // Words that start a word in the name rank first ("jpx" before "...ajpx").
        const score = terms.reduce((n, t) => n + (new RegExp(`(^|\\s)${t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`).test(h.hay) ? 1 : 0), 0);
        found.push({ h, score });
      });
      found.sort((a, b) => b.score - a.score
        || (this.words.clubs === 'wedges' // wedge builds list wedge models before iron sets
          ? Number(b.h.wedge) - Number(a.h.wedge)
          : Number(a.h.wedge) - Number(b.h.wedge))
        || b.h.end - a.h.end
        || `${a.h.brand} ${a.h.model}`.localeCompare(`${b.h.brand} ${b.h.model}`));
      return found.map((f) => f.h);
    }

    /** Draws the clubhead step for the current mode: search + results, selected row, or typed-in box. */
    renderHead() {
      const search = this.el.headSearch;
      if (!search) return;
      const listMode = this.headMode === 'list';
      const open = listMode && (this.headOpen || !this.head);

      search.hidden = !open;
      search.setCustomValidity(listMode && !this.head ? 'Choose your clubhead from the list.' : '');
      search.required = listMode;
      this.el.clubhead.hidden = listMode;
      this.el.clubhead.required = !listMode;
      this.el.headPicked.hidden = !(listMode && this.head && !open);
      if (this.head) this.el.headPickedName.textContent = headLabel(this.head);

      const sw = this.el.headSwitch;
      sw.hidden = !listMode && this.headsState === 'failed'; // no list to go back to
      sw.textContent = listMode ? `My ${this.words.clubs} aren’t listed` : 'Search the list instead';

      const list = this.el.headResults;
      const status = this.el.headStatus;
      status.hidden = !open;
      const query = search.value.trim();
      let found = [];
      if (!open) {
        status.textContent = '';
      } else if (this.headsState === 'loading' || !this.headsState) {
        status.textContent = query ? 'Loading the clubhead list…' : 'Start typing your brand or model.';
      } else if (!query) {
        status.textContent = 'Start typing your brand or model.';
      } else {
        found = this.findHeads(query);
        if (!found.length) status.textContent = `No matches. Try fewer letters, or choose “My ${this.words.clubs} aren’t listed”.`;
        else if (found.length > HEAD_RESULTS_MAX) status.textContent = `${found.length} matches. Keep typing to narrow the list.`;
        else status.textContent = `${found.length} match${found.length === 1 ? '' : 'es'}`;
      }

      list.innerHTML = '';
      found.slice(0, HEAD_RESULTS_MAX).forEach((h) => {
        const isPicked = this.head === h;
        const row = el('button', `build-result${isPicked ? ' is-selected' : ''}`);
        row.type = 'button';
        row.dataset.i = String(h.id);
        row.setAttribute('aria-pressed', String(isPicked));
        row.appendChild(el('span', 'build-result__name', `${h.brand} ${h.model}`));
        row.appendChild(el('span', 'build-result__meta', [h.wedge ? 'Wedge' : '', h.years].filter(Boolean).join(' · ')));
        list.appendChild(row);
      });
      this.el.headResultsWrap.hidden = !found.length;
      list.hidden = !found.length;
      list.scrollTop = 0;
      this.updateHeadMore();
    }

    /** Brand / model as text: the picked list entry, or what was typed in. */
    headText() {
      if (!this.el.headSearch) return '';
      if (this.headMode === 'list') return this.head ? headLabel(this.head) : '';
      return (this.el.clubhead.value || '').replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
    }

    /** Quantity box value, 1 to 99. */
    shaftQty() {
      if (!this.el.qty) return 1;
      const n = Math.floor(Number(this.el.qty.value));
      return Math.min(99, Math.max(1, Number.isFinite(n) ? n : 1));
    }

    headClubsText() {
      if (this.qtyMode) return clubsSummary(this.el.clubs.filter((c) => c.checked).map((c) => c.value));
      if (!this.el.headClubs) return '';
      return (this.el.headClubs.value || '').replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
    }

    /** Keeps the Iron #s box in step with the club buttons until the customer edits it. */
    syncHeadClubs() {
      const box = this.el.headClubs;
      if (!box) return;
      const clubs = this.clubButtons.filter((c) => this.selectedClubs.has(c.key)).map((c) => c.club);
      const auto = clubsSummary(clubs);
      if (!box.value.trim()) this.headClubsEdited = false;
      if (!this.headClubsEdited && box.value !== auto) box.value = auto;
      this.headClubsAuto = auto;
    }

    /** This shaft's tip size ('0.355' / '0.370'), from the selected shafts or any variant. */
    shaftTip(groups) {
      const v = (groups || []).map((g) => g.variant).find((x) => x && tipKey(x.tip))
        || this.data.shaft.variants.find((x) => tipKey(x.tip));
      return v ? tipKey(v.tip) : '';
    }

    /**
     * Warns (never blocks) when the picked clubhead's tip size, per our fitting chart,
     * differs from this shaft's. Links to the same shaft in the other tip size when we
     * have it in stock, otherwise invites them to text us.
     */
    checkHeadTip(groups) {
      const box = this.el.headTip;
      if (!box) return;
      // Only for a clubhead that is picked (not while the customer is searching for another one).
      const head = this.headMode === 'list' && !this.headOpen ? this.head : null;
      const shaftTip = this.shaftTip(groups);
      const headTip = head ? head.tip : '';
      const key = head && headTip && shaftTip && headTip !== shaftTip ? `${head.id}|${shaftTip}` : '';
      if (key === this.headTipKey) return;
      this.headTipKey = key;
      box.textContent = '';
      box.hidden = !key;
      if (!key) return;

      box.appendChild(el('strong', null, 'Tip size check: '));
      box.appendChild(document.createTextNode(
        `our fitting chart lists the ${head.brand} ${head.model} with a ${headTip}" tip, and this shaft has a ${shaftTip}" tip. `
      ));
      const follow = el('span');
      box.appendChild(follow);
      const contact = () => {
        follow.textContent = 'Questions? Text us at ';
        const a = el('a', null, TEXT_US.label);
        a.href = TEXT_US.href;
        follow.append(a, document.createTextNode('.'));
      };
      contact();
      this.otherTipShaft(headTip).then((match) => {
        if (this.headTipKey !== key || !match) return;
        follow.textContent = `This shaft also comes with a ${headTip}" tip: `;
        const a = el('a', null, match.title);
        a.href = match.url;
        follow.append(a, document.createTextNode('. Or text us at '));
        const t = el('a', null, TEXT_US.label);
        t.href = TEXT_US.href;
        follow.append(t, document.createTextNode(' with questions.'));
      });
    }

    /** The same shaft in the other tip size, in stock, from iron_shafts.json; null when there is none. */
    otherTipShaft(tip) {
      this.otherTipCache = this.otherTipCache || new Map();
      if (!this.otherTipCache.has(tip)) {
        const own = this.data.shaft;
        this.otherTipCache.set(tip, loadJson(this.data.shaftsUrl).then(async (cat) => {
          const products = cat && Array.isArray(cat.products) ? cat.products : [];
          const mine = products.find((p) => p && p.handle === own.handle);
          const family = shaftFamily(mine ? mine.title : own.title);
          if (!family) return null;
          const seen = new Set();
          const picks = products
            .filter((p) => p && p.handle && p.handle !== own.handle && tipKey(p.tip) === tip
              && p.inStock === true && p.recommendable !== false && shaftFamily(p.title) === family)
            .sort((a, b) => (Number(b.sales90) || 0) - (Number(a.sales90) || 0))
            .filter((p) => (seen.has(p.handle) ? false : seen.add(p.handle)));
          for (const p of picks) {
            const live = await productJson(p.handle); // eslint-disable-line no-await-in-loop
            if (live && live.available) {
              const u = new URL(`${ROOT}products/${encodeURIComponent(p.handle)}`, window.location.origin);
              const view = new URLSearchParams(window.location.search).get('view');
              if (view) u.searchParams.set('view', view); // keep the same builder page
              return { title: live.title || p.title, url: u.pathname + u.search };
            }
          }
          return null;
        }).catch(() => null));
      }
      return this.otherTipCache.get(tip);
    }

    fillGripVariants() {
      const product = this.selectedGripProduct();
      const sel = this.el.gripVariant;
      sel.innerHTML = '';

      if (!product) {
        sel.hidden = true;
        return;
      }

      // Only one size / color: nothing to choose, so pick it for the customer.
      if (product.variants.length === 1) {
        sel.appendChild(option(String(product.variants[0].id), product.variants[0].title));
        sel.value = String(product.variants[0].id);
        sel.hidden = true;
        return;
      }

      sel.appendChild(option('', 'Choose size / color'));
      product.variants.forEach((v) => {
        const soldOut = buyableQty(v) === 0;
        const label = `${v.title} (${formatMoney(v.price, this.data.moneyFormat)})${soldOut ? ' - sold out' : ''}`;
        sel.appendChild(option(String(v.id), label, soldOut));
      });
      sel.hidden = false;
    }

    /**
     * Shows each short <select data-build-choices> as a row of pill buttons.
     * The select stays in the page (hidden) and keeps holding the value.
     */
    enhanceChoices() {
      this.querySelectorAll('select[data-build-choices]').forEach((sel) => {
        const group = el('div', 'build-choices');
        group.setAttribute('role', 'group');
        if (sel.getAttribute('aria-label')) group.setAttribute('aria-label', sel.getAttribute('aria-label'));

        const sync = () => group.querySelectorAll('.build-choice').forEach((b) => {
          const on = b.dataset.value === sel.value;
          const opt = Array.from(sel.options).find((o) => o.value === b.dataset.value);
          b.classList.toggle('is-selected', on);
          b.setAttribute('aria-pressed', String(on));
          b.disabled = Boolean(opt && opt.disabled);
          b.title = (opt && opt.disabled && opt.title) || '';
        });
        sel.buildSync = sync; // lets update() redraw the buttons after it disables an option

        Array.from(sel.options).forEach((o) => {
          const b = el('button', 'build-choice', o.textContent.trim());
          b.type = 'button';
          b.dataset.value = o.value;
          b.addEventListener('click', () => {
            if (b.disabled || sel.value === o.value) return;
            sel.value = o.value;
            sel.dispatchEvent(new Event('change', { bubbles: true }));
          });
          group.appendChild(b);
        });

        sel.addEventListener('change', sync);
        sel.hidden = true;
        sel.insertAdjacentElement('afterend', group);
        sync();
      });
    }

    /* ----- events ----- */

    bind() {
      const onChange = () => this.update();

      // "Need help installing these shafts? (Add retrofit service)"
      if (this.el.retrofitToggle) {
        this.el.retrofitToggle.addEventListener('click', () => {
          // Quantity mode: keep the shafts-only quantity to put back when the add-on is closed again.
          if (this.el.qty && !this.retrofit) this.qtyBeforeRetrofit = this.shaftQty();
          this.retrofit = !this.retrofit;
          if (this.el.qty && !this.retrofit) this.el.qty.value = String(this.qtyBeforeRetrofit || 1);
          this.el.panel.hidden = !this.retrofit;
          this.el.retrofitToggle.setAttribute('aria-expanded', String(this.retrofit));
          if (this.retrofit) this.loadHeads(); // fetch the clubhead list while they work through the steps
          this.showError('');
          this.update();
        });
      }

      // Shaft option pills (event delegation; rows are re-rendered).
      this.el.optionRows.addEventListener('click', (evt) => {
        const b = evt.target.closest('.build-btn');
        if (!b) return;
        this.selected[Number(b.dataset.optionPos)] = b.dataset.value;
        this.renderOptionRows();
        this.renderSpecs();
        this.renderClubGrid(); // drops clubs that are sold out in the new flex
        this.update();
      });

      if (this.el.clubGrid) {
        this.el.clubGrid.addEventListener('click', (evt) => {
          const b = evt.target.closest('.build-btn--club');
          if (!b || b.disabled) return;
          const key = b.dataset.clubKey;
          if (this.selectedClubs.has(key)) this.selectedClubs.delete(key);
          else this.selectedClubs.add(key);
          this.renderClubGrid();
          this.update();
        });
      }

      this.el.clubs.forEach((c) => c.addEventListener('change', onChange));

      // Quantity box (quantity mode, shafts only). With the retrofit add-on it follows the Iron #s instead.
      if (this.el.qty) {
        const step = (n) => {
          if (this.el.qty.disabled) return;
          this.el.qty.value = String(this.shaftQty() + n);
          this.update();
        };
        this.el.qtyMinus.addEventListener('click', () => step(-1));
        this.el.qtyPlus.addEventListener('click', () => step(1));
        this.el.qty.addEventListener('input', onChange);
        this.el.qty.addEventListener('change', () => {
          this.el.qty.value = String(this.shaftQty());
          this.update();
        });
      }
      this.el.quick.forEach((btn) => btn.addEventListener('click', () => {
        const wanted = btn.dataset.clubs ? btn.dataset.clubs.split(',') : [];
        this.el.clubs.forEach((c) => { c.checked = wanted.includes(c.value); });
        this.update();
      }));

      [this.el.length, this.el.logo, this.el.tape, this.el.removed, this.el.adapter, this.el.gripVariant,
        this.el.flo, this.el.ackCancel, this.el.ackTurnaround, ...this.el.protection]
        .filter(Boolean)
        .forEach((s) => s.addEventListener('change', onChange));
      if (this.el.clubhead) this.el.clubhead.addEventListener('input', onChange);

      this.el.brand.addEventListener('change', () => { this.fillModels(); this.update(); });
      if (this.el.gripSearch) {
        this.el.gripSearch.addEventListener('input', () => {
          this.gripPickerOpen = true;
          this.renderGripResults();
        });
      }
      this.el.notes.addEventListener('input', () => this.updateNotesCount());
      this.el.model.addEventListener('change', () => {
        this.gripPickerOpen = !this.el.model.value; // picked one: fold the list away
        this.renderGripResults();
        this.fillGripVariants();
        this.update();
      });
      this.el.add.addEventListener('click', () => this.addToCart());

      // Browser back/forward can restore stale form state.
      window.addEventListener('pageshow', (evt) => {
        if (!evt.persisted) return;
        this.busy = false;
        this.el.add.classList.remove('is-loading');
        this.update();
      });
    }

    /* ----- state ----- */

    /**
     * Selected shafts grouped by variant, in club order:
     * [{ variant, clubs: ['#4 iron', ...] }]
     */
    shaftGroups() {
      const groups = new Map();
      const add = (variant, club) => {
        const key = variant ? variant.id : 'none';
        if (!groups.has(key)) groups.set(key, { variant, clubs: [] });
        groups.get(key).clubs.push(club);
      };

      if (this.qtyMode && !this.retrofit) {
        // Shafts only: one line of the selected flex, quantity from the Quantity box.
        // (Blank club names: there are no clubs to label until the retrofit add-on is used.)
        const v = this.variantFor(null);
        for (let i = 0; i < this.shaftQty(); i++) add(v, '');
      } else if (this.clubPos) {
        this.clubButtons
          .filter((c) => this.selectedClubs.has(c.key))
          .forEach((c) => add(this.variantFor(c.value), c.club));
      } else {
        const v = this.variantFor(null);
        this.el.clubs.filter((c) => c.checked).forEach((c) => add(v, c.value));
      }
      return Array.from(groups.values());
    }

    clubCount(groups) {
      return (groups || this.shaftGroups()).reduce((n, g) => n + g.clubs.length, 0);
    }

    selectedGripProduct() {
      const id = Number(this.el.model.value);
      return id ? this.data.grips.find((g) => g.productId === id) : null;
    }

    selectedGrip() {
      const product = this.selectedGripProduct();
      const id = Number(this.el.gripVariant.value);
      if (!product || !id) return null;
      const variant = product.variants.find((v) => v.id === id);
      return variant ? { product, variant } : null;
    }

    noGrip() {
      return this.el.brand.value === NONE;
    }

    selectedAdapter() {
      if (!this.el.adapter) return null;
      const id = Number(this.el.adapter.value);
      return id ? this.data.adapters.find((a) => a.id === id) || null : null;
    }

    /** The paid grip tape variant, or null for the free "1 WRAP DEFAULT". */
    selectedTape() {
      if (!this.el.tape || !this.el.tape.value) return null;
      return (this.data.tape || []).find((t) => String(t.id) === this.el.tape.value) || null;
    }

    /** Grip tape choice as text, e.g. "1 WRAP DEFAULT" or "2 WRAPS". */
    tapeLabel() {
      const tape = this.selectedTape();
      if (tape) return tape.title;
      return this.el.tape && this.el.tape.options.length ? this.el.tape.options[0].textContent.trim() : '';
    }

    /** The FLO variant when the customer chose YES, otherwise null. */
    selectedFlo() {
      if (!this.el.flo || !this.data.flo) return null;
      return this.el.flo.value === 'YES' ? this.data.flo : null;
    }

    /**
     * Shipping protection choice: a protection variant, null for "NO Coverage",
     * or undefined when nothing is selected yet (or the step is not shown).
     */
    selectedProtection() {
      const checked = this.el.protection.find((r) => r.checked);
      if (!checked) return undefined;
      if (!checked.value) return null;
      return (this.data.protection || []).find((p) => String(p.id) === checked.value) || null;
    }

    /** Clubhead for the build team, e.g. "Mizuno JPX 923 Tour (2022) / 4-PW". */
    clubheadText() {
      if (this.el.headSearch) {
        return [this.headText(), this.headClubsText()].filter(Boolean).join(' / ').slice(0, 100);
      }
      if (!this.el.clubhead) return '';
      return (this.el.clubhead.value || '').replace(/<[^>]*>/g, '').trim().slice(0, 100);
    }

    /**
     * Too few in stock for the clubs picked on one shaft. Clubs that share a shaft
     * (PW, GW, SW, LW on a "Wedge" variant) get a message naming them, e.g.
     * "PW, GW and SW share the same shaft SKU and only 2 are in stock. Remove one wedge."
     */
    static shortStockMessage(g, qtyMode) {
      const left = buyableQty(g.variant);
      if (qtyMode) return `Only ${left} of ${g.variant.title} in stock. Choose ${left === 1 ? '1 shaft' : `${left} shafts or fewer`}.`;
      if (g.clubs.length < 2) return `Only ${left} left of ${g.variant.title}.`;
      const names = `${g.clubs.slice(0, -1).join(', ')} and ${g.clubs[g.clubs.length - 1]}`;
      const extra = g.clubs.length - left;
      const noun = g.clubs.every((c) => WEDGE_CLUBS.includes(c)) ? 'wedge' : 'club';
      const remove = extra === 1 ? `one ${noun}` : `${extra} ${noun}s`;
      return `${names} share the same shaft SKU and only ${left} ${left === 1 ? 'is' : 'are'} in stock. Remove ${remove}.`;
    }

    /** Problems with the selected shafts themselves (combination missing, sold out, too few in stock). */
    shaftProblems(groups) {
      const problems = [];
      groups.forEach((g) => {
        if (!g.variant) problems.push('That shaft combination is not available.');
        else if (buyableQty(g.variant) === 0) problems.push(`${g.variant.title} is sold out.`);
        else if (g.clubs.length > buyableQty(g.variant)) problems.push(BuildConfigurator.shortStockMessage(g, this.qtyMode));
      });
      return problems;
    }

    /** Returns a list of problems; an empty list means the build can be added. */
    validate() {
      const problems = [];
      const groups = this.shaftGroups();
      const qty = this.clubCount(groups);

      if (qty === 0) {
        if (this.qtyMode) problems.push(`Pick the ${this.words.nums} these shafts are going into.`);
        else problems.push(this.clubPos ? 'Select one or more club numbers.' : 'Select at least one club.');
      }
      problems.push(...this.shaftProblems(groups));

      // Everything below only applies to the retrofit service (grip + assembly).
      if (!this.retrofit) return problems;

      if (!this.el.length.value) problems.push('Choose a playing length.');

      if (this.el.headSearch) {
        if (!this.headText()) {
          problems.push(this.headMode === 'list'
            ? `Find your clubhead brand / model, or choose "My ${this.words.clubs} aren’t listed".`
            : 'Enter your clubhead brand / model.');
        }
        if (!this.qtyMode && !this.headClubsText()) problems.push(`Enter the ${this.words.nums} these shafts are going into.`);
      } else if (this.el.clubhead && !this.clubheadText()) {
        problems.push(`Enter your clubhead brand / model / ${this.words.nums}.`);
      }

      if (!this.noGrip()) {
        const grip = this.selectedGrip();
        if (!grip) {
          problems.push('Choose a grip, or "No grip".');
        } else {
          const canBuy = buyableQty(grip.variant);
          if (canBuy === 0) problems.push('That grip is sold out.');
          else if (qty > canBuy) problems.push(`Only ${canBuy} of that grip left. Choose fewer clubs or another grip.`);
        }
      }

      if (this.el.adapter) {
        const adapter = this.selectedAdapter();
        if (!adapter) problems.push('Choose an adapter.');
        else if (qty > buyableQty(adapter)) problems.push('Not enough of that adapter in stock.');
      }

      if (this.el.protection.length && this.selectedProtection() === undefined) {
        problems.push('Choose a shipping protection option.');
      }

      if (this.el.ackCancel && !this.el.ackCancel.checked) {
        problems.push('Please acknowledge the cancellation & return policy.');
      }
      if (this.el.ackTurnaround && !this.el.ackTurnaround.checked) {
        problems.push('Please acknowledge the order turnaround.');
      }

      if (this.data.labor && !this.data.labor.available) {
        problems.push('Assembly is unavailable right now. Please contact us.');
      }

      return problems;
    }

    /* ----- render ----- */

    update() {
      const fmt = (c) => formatMoney(c, this.data.moneyFormat);
      const groups = this.shaftGroups();
      const qty = this.clubCount(groups);
      const shaftsTotal = groups.reduce((s, g) => s + (g.variant ? g.variant.price * g.clubs.length : 0), 0);
      const grip = this.noGrip() ? null : this.selectedGrip();
      const tape = grip ? this.selectedTape() : null; // grip tape only applies when we install a grip
      const adapter = this.selectedAdapter();
      const labor = this.data.labor;
      const flo = this.selectedFlo();
      const protection = this.selectedProtection() || null;

      // Selected shafts list (like the KBS retrofit pages).
      this.el.shaftsList.innerHTML = '';
      groups.forEach((g) => {
        if (!g.variant) return;
        g.clubs.forEach((club) => {
          const wedgeShared = !g.variant.title.toLowerCase().includes(club.toLowerCase());
          const label = `${g.variant.title}${wedgeShared ? ` (${club})` : ''} — ${fmt(g.variant.price)}`;
          this.el.shaftsList.appendChild(el('div', 'build-shafts__row', label));
        });
      });
      this.el.shafts.hidden = qty === 0 || this.qtyMode; // quantity mode: the Quantity box says it
      this.el.shaftsCount.textContent = `${qty} shaft${qty === 1 ? '' : 's'} selected`;
      this.el.shaftsPrice.textContent = fmt(shaftsTotal);

      // Main price: shafts total when clubs are picked, otherwise the selected flex's price.
      if (this.el.mainPrice) {
        const fallback = this.data.shaft.variants.find((v) => v.options.every((val, i) => (i + 1 === this.clubPos) || val === this.selected[i + 1]));
        const shown = qty && !this.qtyMode ? shaftsTotal : (fallback ? fallback.price : null); // quantity mode: price each
        if (shown != null) this.el.mainPrice.textContent = fmt(shown);
      }

      // Grip extras only make sense with a grip.
      this.el.gripExtras.hidden = !grip;
      this.limitAlignLogo();
      if (grip) {
        const canBuy = buyableQty(grip.variant);
        this.el.gripNote.hidden = !(canBuy !== Infinity && canBuy < 10);
        this.el.gripNote.textContent = canBuy === Infinity ? '' : `Only ${canBuy} left in stock.`;
      } else {
        this.el.gripNote.hidden = true;
      }

      // Build breakdown (display only; checkout uses Shopify's prices).
      const rows = [];
      rows.push([`Shafts (${qty})`, fmt(shaftsTotal)]);
      if (grip) rows.push([`Grip: ${grip.product.title}`, `${fmt(grip.variant.price)} × ${qty}`]);
      if (tape) rows.push([`Grip tape: ${tape.title}`, `${fmt(tape.price)} × ${qty}`]);
      if (adapter) rows.push(['Adapter', `${fmt(adapter.price)} × ${qty}`]);
      if (flo) rows.push(['FLO (shaft alignment)', `${fmt(flo.price)} × ${qty}`]);
      if (labor) rows.push(['Assembly', `${fmt(labor.price)} × ${qty}`]);
      if (protection) rows.push([`Shipping protection: ${protection.title}`, `${fmt(protection.price)} × ${qty}`]);
      this.el.lines.innerHTML = '';
      rows.forEach(([label, value]) => this.el.lines.append(el('dt', null, label), el('dd', null, value)));

      const perClubExtras = (grip ? grip.variant.price : 0) + (tape ? tape.price : 0) + (adapter ? adapter.price : 0)
        + (flo ? flo.price : 0) + (labor ? labor.price : 0) + (protection ? protection.price : 0);
      const total = shaftsTotal + perClubExtras * qty;
      this.el.total.textContent = qty ? fmt(total) : '-';

      // Quantity mode: with the retrofit add-on the quantity is the number of Iron #s picked.
      if (this.qtyMode && this.el.qty) {
        this.el.qty.disabled = this.retrofit;
        this.el.qtyMinus.disabled = this.retrofit || this.shaftQty() <= 1;
        this.el.qtyPlus.disabled = this.retrofit || this.shaftQty() >= 99;
        this.el.qty.closest('.js-build-qty-wrap').title = this.retrofit ? `Set by the ${this.words.nums} you pick` : '';
        if (this.retrofit) this.el.qty.value = String(qty);
        else if (this.el.qty.value === '0') this.el.qty.value = '1';
      }
      if (this.el.ironsRequired) this.el.ironsRequired.checked = qty > 0;

      // Clubhead step: Iron #s follow the club buttons; tip size warning.
      this.syncHeadClubs();
      this.checkHeadTip(groups);

      // Button state + hint.
      const problems = this.validate();
      const ready = problems.length === 0 && !this.busy;
      this.el.add.disabled = !ready;
      this.el.add.setAttribute('aria-disabled', String(!ready));
      this.el.add.textContent = this.retrofit && this.el.retrofitToggle ? 'Add shafts + retrofit to cart' : 'Add to cart';
      const summary = this.retrofit
        ? `${qty} club${qty === 1 ? '' : 's'} · build total ${fmt(total)}`
        : `${qty} shaft${qty === 1 ? '' : 's'} · ${fmt(shaftsTotal)}`;
      // A shaft stock problem is shown under the club buttons; the line under Add to cart points to it.
      // (Quantity mode's note is in the retrofit panel, so shafts-only problems stay under Add to cart.)
      const noteShown = Boolean(this.el.shaftNote) && (!this.qtyMode || this.retrofit);
      const shaftIssue = noteShown ? this.shaftProblems(groups)[0] : null;
      if (this.el.shaftNote) {
        this.el.shaftNote.textContent = shaftIssue || '';
        this.el.shaftNote.hidden = !shaftIssue;
      }
      this.el.hint.textContent = shaftIssue && problems[0] === shaftIssue
        ? `See the note under the ${this.qtyMode ? (this.words.clubs === 'wedges' ? 'wedges' : 'Iron #s') : 'club numbers'}.`
        : problems[0] || summary;
      this.showGripPhoto();
    }

    /**
     * Shows the chosen grip's photo under the selected-grip row, turned horizontal.
     * Only that one photo is loaded, after the grip is picked, so the list stays fast.
     */
    showGripPhoto() {
      const box = this.el.gripPhoto;
      if (!box) return;
      const grip = this.el.gripPicked.hidden ? null : this.selectedGripProduct();
      const variantId = grip ? Number(this.el.gripVariant.value) || 0 : 0;
      const key = grip ? `${grip.productId}:${variantId}` : '';
      if (key === this.gripPhotoKey) return;
      this.gripPhotoKey = key;
      box.hidden = true;
      box.textContent = '';
      // No photo until the size / color is chosen (single-variant grips are chosen automatically).
      if (!grip || !grip.handle || !variantId) return;

      gripPhoto(grip, variantId).then((canvas) => {
        if (this.gripPhotoKey !== key || !canvas) return; // the customer moved on, or no usable photo
        canvas.setAttribute('role', 'img');
        canvas.setAttribute('aria-label', grip.title);
        box.appendChild(canvas);
        box.hidden = false;
      });
    }

    /**
     * ALIGN grips (any grip with "Align" in its title) can only be installed logo up,
     * so "Down" is greyed out and the choice is set back to "Up".
     */
    limitAlignLogo() {
      const sel = this.el.logo;
      if (!sel) return;
      const product = this.noGrip() ? null : this.selectedGripProduct();
      const align = Boolean(product) && /align/i.test(product.title);
      const down = Array.from(sel.options).find((o) => o.value === 'Down');
      if (!down || down.disabled === align) return;
      down.disabled = align;
      down.title = align ? 'ALIGN grips are installed logo up only' : '';
      if (align && sel.value === 'Down') sel.value = 'Up';
      if (sel.buildSync) sel.buildSync();
    }

    /** Character counter under Build notes, so customers can see the limit before they reach it. */
    updateNotesCount() {
      if (!this.el.notesCount) return;
      const used = this.el.notes.value.length;
      const atLimit = used >= this.notesMax;
      this.el.notesCount.textContent = used
        ? `${used} / ${this.notesMax} characters${atLimit ? ' - limit reached' : ''}`
        : `Up to ${this.notesMax} characters`;
      this.el.notesCount.classList.toggle('is-limit', atLimit);
    }

    showError(message) {
      this.el.error.textContent = message;
      this.el.error.hidden = !message;
    }

    /* ----- add to cart ----- */

    /**
     * Work instructions for the build team (iron / wedge retrofit builds only).
     * Saved as line-item properties on the lead shaft, so they show at checkout,
     * on the order and on packing slips. snippets/cart-items.liquid hides them in
     * the cart (any "NN_" / "A_" property on a build line).
     */
    buildInstructions(grip, qty, protection, flo, notes) {
      if (!this.el.retrofitToggle) return {};
      const ungripped = 'Leave Un-gripped';
      const steps = {};
      // Only when they apply: the customer's build notes (as written), and the FLO add-on.
      if (notes) steps['00_CUSTOM'] = notes;
      if (flo) steps['00_'] = 'PERFORM FLO ALIGNMENT yes';
      Object.assign(steps, {
        '00_REMOVE/CLEAN/INSPECT': this.clubheadText(),
        // custom-build-370 (data.tipTrim): "01_PREP TIP & TRIM" with the per-club tip trim.
        [this.data.tipTrim ? '01_PREP TIP & TRIM' : '01_PREP TIP']: this.data.tipTrim
          ? tipTrimText(this.data.vendor, this.shaftGroups().flatMap((g) => g.clubs).filter(Boolean))
          : 'NO TRIM',
        '02_RE-SHAFT': 'Epoxy Ferrule + Shaft LOGO DOWN onto clubhead',
        '03_STD PLAY LENGTH (BUILDER HIGHLIGHT)':
          '/ 3i-39" / 4i-38.5" / 5i-38" / 6i-37.5" / 7i-37" / 8i-36.5" / 9i-36" / PW-35.5" /',
        '04_Grip Tape-1': grip ? `${this.tapeLabel()} * ${qty}` : ungripped,
        '05_Grip': grip ? `Logo ${this.el.logo.value} with clubhead installed square` : ungripped,
        '06_FINAL QC': 'Verify Specs'
      });
      if (this.el.removed) steps['A_REMOVED COMPONENTS'] = this.el.removed.value;
      if (this.el.protection.length) {
        steps['A_Shipping Protection'] = protection ? protection.title : 'NO Coverage';
      }
      return steps;
    }

    buildItems(buildId) {
      const groups = this.shaftGroups();

      // Shafts only (retrofit not requested): plain lines, no build linkage.
      // Wedge clubs that share one variant keep a visible "Clubs" note (e.g. "PW, GW").
      if (!this.retrofit) {
        return groups.map((g) => {
          const shared = g.clubs.some((club) => !g.variant.title.toLowerCase().includes(club.toLowerCase()));
          const item = { id: g.variant.id, quantity: g.clubs.length };
          if (shared) item.properties = { Clubs: g.clubs.join(', ') };
          return item;
        });
      }

      const qty = this.clubCount(groups);
      const allClubs = groups.flatMap((g) => g.clubs);
      const grip = this.noGrip() ? null : this.selectedGrip();
      const tape = grip ? this.selectedTape() : null;
      const adapter = this.selectedAdapter();
      const labor = this.data.labor;
      const flo = this.selectedFlo();
      const protection = this.selectedProtection() || null;
      const lead = groups[0];
      const leadId = lead.variant.id;

      const child = (role) => ({ _build_id: buildId, _build_role: role, _build_parent: String(leadId) });

      const items = [];
      if (grip) items.push({ id: grip.variant.id, quantity: qty, properties: child('grip') });
      if (tape) items.push({ id: tape.id, quantity: qty, properties: child('tape') });
      if (adapter) items.push({ id: adapter.id, quantity: qty, properties: child('adapter') });
      if (flo) items.push({ id: flo.id, quantity: qty, properties: child('flo') });
      if (protection) items.push({ id: protection.id, quantity: qty, properties: child('protection') });
      if (labor) items.push({ id: labor.id, quantity: qty, properties: child('labor') });

      // Extra shaft variants (one line per variant; wedge clubs that share a variant share a line).
      groups.slice(1).forEach((g) => {
        items.push({
          id: g.variant.id,
          quantity: g.clubs.length,
          properties: { Clubs: g.clubs.join(', '), ...child('shaft_club') }
        });
      });

      const gripLabel = grip
        ? `${grip.product.title}${/default title/i.test(grip.variant.title) ? '' : ` - ${grip.variant.title}`}`
        : 'None (customer supplied)';

      const notes = (this.el.notes.value || '').replace(/<[^>]*>/g, '').trim().slice(0, this.notesMax);

      const leadProps = {
        'Build Type': this.data.buildType,
        Clubs: lead.clubs.join(', '),
        'Build Clubs': groups.length > 1 ? allClubs.join(', ') : '',
        ...this.buildInstructions(grip, qty, protection, flo, notes),
        'Playing Length': this.el.length.value,
        'FLO (Shaft Alignment)': this.el.flo ? (flo ? 'YES' : 'NO') : '',
        'Clubhead Brand/Model/Iron #s': this.clubheadText(),
        Grip: gripLabel,
        'Grip Logo': grip ? this.el.logo.value : '',
        'Grip Tape': grip ? this.tapeLabel() : '',
        Adapter: adapter ? adapter.title : '',
        'Shipping Protection (per club)': this.el.protection.length ? (protection ? protection.title : 'NO Coverage') : '',
        Notes: notes,
        'Removed Components': this.el.removed ? this.el.removed.value : '',
        'Cancellation & Return Policy Acknowledgement': this.el.ackCancel && this.el.ackCancel.checked ? 'YES' : '',
        'Order Turnaround Acknowledgement': this.el.ackTurnaround && this.el.ackTurnaround.checked ? 'YES' : '',
        _build_id: buildId,
        _build_role: 'shaft'
      };
      Object.keys(leadProps).forEach((k) => {
        if (leadProps[k] === '' || leadProps[k] == null) delete leadProps[k];
      });

      // Lead shaft goes last.
      items.push({ id: leadId, quantity: lead.clubs.length, properties: leadProps });
      return items;
    }

    sectionsToRender() {
      const sections = ['cart-icon-bubble'];
      const drawer = document.querySelector('cart-drawer');
      const drawerSection = drawer && drawer.closest('.shopify-section');
      if (drawerSection) sections.push(drawerSection.id.replace('shopify-section-', ''));
      return sections;
    }

    async addToCart() {
      if (this.busy) return;
      const problems = this.validate();
      if (problems.length) {
        this.showError(problems[0]);
        return;
      }

      this.busy = true;
      this.showError('');
      this.el.add.disabled = true;
      this.el.add.classList.add('is-loading');

      const buildId = makeBuildId();
      const items = this.buildItems(buildId);

      try {
        const res = await fetch(`${ROOT}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items,
            sections: this.sectionsToRender(),
            sections_url: window.location.pathname
          })
        });
        const data = await res.json();

        if (!res.ok) {
          await BuildConfigurator.removeBuild(buildId);
          throw new Error(data.description || data.message || 'Could not add this build to your cart.');
        }

        await this.afterAdd(data, buildId, items);
      } catch (err) {
        console.error('[build]', err); // eslint-disable-line
        this.showError(err.message || 'Could not add this build to your cart.');
        this.dispatchEvent(new CustomEvent('on:cart:error', { bubbles: true, detail: { error: err.message } }));
      } finally {
        this.busy = false;
        this.el.add.classList.remove('is-loading');
        this.update();
      }
    }

    async afterAdd(data, buildId, items) {
      const settings = (window.theme && window.theme.settings) || {};
      const cartUrl = (window.theme && window.theme.routes && window.theme.routes.cart) || `${ROOT}cart`;
      const drawer = document.querySelector('cart-drawer');

      // Let other scripts (analytics, etc.) know a build was added.
      document.dispatchEvent(new CustomEvent('build:added', {
        detail: { buildId, items, response: data }
      }));

      if (settings.afterAtc === 'page' || !drawer || typeof drawer.renderContents !== 'function') {
        window.location.href = cartUrl;
        return;
      }

      await drawer.renderContents(data, settings.afterAtc === 'drawer');

      try {
        const cart = await (await fetch(`${ROOT}cart.js`)).json();
        const lead = items[items.length - 1];
        this.dispatchEvent(new CustomEvent('on:cart:add', {
          bubbles: true,
          detail: { cart, variantId: lead && lead.id }
        }));
      } catch (e) { /* non-critical */ }
    }

    /** Removes every line carrying this build id (used to undo a partial add). */
    static async removeBuild(buildId) {
      try {
        const cart = await (await fetch(`${ROOT}cart.js`)).json();
        const updates = {};
        cart.items.forEach((item) => {
          if (item.properties && item.properties._build_id === buildId) updates[item.key] = 0;
        });
        if (!Object.keys(updates).length) return;
        await fetch(`${ROOT}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates })
        });
      } catch (e) {
        console.error('[build] cleanup failed', e); // eslint-disable-line
      }
    }
  }

  customElements.define('build-configurator', BuildConfigurator);
})();
